import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Eye, Leaf, Loader2, Nut, Trophy } from "lucide-react";
import type { CompetitionConfig, Event } from "@/lib/event-schema";
import { api, type CompetitionPublicEntry } from "@/lib/api-client";
import { cn } from "@/lib/cn";

type Props = {
  config: CompetitionConfig;
  event: Event;
};

export function CompetitionModule({ config, event }: Props) {
  const initialValues: Record<string, string | boolean> = Object.fromEntries(
    config.fields.map((f) => [f.id, f.type === "boolean" ? false : ""]),
  );
  const [values, setValues] = useState<Record<string, string | boolean>>(initialValues);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [entries, setEntries] = useState<CompetitionPublicEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const headline = useMemo(
    () => config.title || `${capitalize(config.entryNoun)} competition`,
    [config.title, config.entryNoun],
  );
  const showEntries = Boolean(config.showEntries);

  useEffect(() => {
    if (!showEntries) return;
    let cancelled = false;
    api.competition
      .list(event.slug)
      .then((data) => {
        if (!cancelled) setEntries(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(String(err.message ?? err));
      });
    return () => {
      cancelled = true;
    };
  }, [event.slug, showEntries]);

  function update(id: string, next: string | boolean) {
    setValues((v) => ({ ...v, [id]: next }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const created = await api.competition.add(event.slug, values);
      setEntries((prev) => (prev ? [...prev, created] : [created]));
      setStatus("success");
      setValues(initialValues);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <section
      id="competition"
      className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${event.slug}-competition`}
    >
      <header className="text-center mb-10">
        <p className="eyebrow text-accent inline-flex items-center gap-2">
          <Trophy className="h-3.5 w-3.5" strokeWidth={1.75} />
          A friendly contest
        </p>
        <h2
          id={`${event.slug}-competition`}
          className="display-title mt-3 text-[clamp(2rem,4.4vw,3.25rem)] leading-tight"
        >
          {headline}
        </h2>
        {config.description ? (
          <p className="mx-auto mt-3 max-w-2xl text-balance text-muted">{config.description}</p>
        ) : null}
        <div className="mx-auto mt-5 flex w-fit items-center gap-2 rounded-full border border-line/70 bg-surface-raised/70 px-3 py-1 text-xs text-muted backdrop-blur">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
          {config.judgingMode === "ranking" ? "Judged by ranking" : "Judged by score"}
        </div>
      </header>

      <div className="grid gap-8 lg:grid-cols-[1.2fr,1.4fr]">
        <aside className="surface-card relative overflow-hidden p-6 lg:p-8">
          <div
            aria-hidden
            className="absolute inset-0 -z-10"
            style={{
              backgroundImage:
                "radial-gradient(circle at 25% 25%, rgb(var(--accent) / 0.20), transparent 50%), radial-gradient(circle at 90% 90%, rgb(var(--accent) / 0.10), transparent 60%)",
            }}
          />
          <p className="eyebrow text-accent">{config.kind ?? config.entryNoun}</p>
          <p className="mt-3 font-display text-2xl font-light leading-snug">
            Bring one {config.entryNoun}, share with the room, watch the room decide.
          </p>
          <ul className="mt-6 grid gap-3 text-sm">
            <Step n={1} text={`Register your ${config.entryNoun} below.`} />
            <Step n={2} text={`Bring it the day of, set up at the ${config.entryNoun} table.`} />
            <Step n={3} text="Visitors taste and vote during the judging window." />
            <Step n={4} text="Winners announced before the closing remarks." />
          </ul>
        </aside>

        <form onSubmit={submit} className="surface-card p-6 lg:p-8">
          {status === "success" ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-accent/15 text-accent">
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-display text-xl font-light">You're entered</h3>
              <p className="mt-1 text-sm text-muted">
                Good luck, we'll see you at the {config.entryNoun} table.
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-6 text-sm text-accent hover:underline"
              >
                Register another entry
              </button>
            </div>
          ) : (
            <div className="grid gap-5">
              {config.fields.map((field) => (
                <FormField
                  key={field.id}
                  field={field}
                  value={values[field.id]}
                  onChange={(v) => update(field.id, v)}
                />
              ))}
              {status === "error" ? (
                <p className="text-sm text-red-600 dark:text-red-400">{errorMsg}</p>
              ) : null}
              <div className="flex items-center justify-end pt-1">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas",
                    "dark:bg-accent dark:text-accent-on",
                    "shadow-glow transition-all hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
                    "disabled:opacity-60",
                  )}
                >
                  {status === "submitting" ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                  )}
                  Enter the competition
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {showEntries ? (
        <EntriesList
          config={config}
          entries={entries}
          loading={entries === null && !loadError}
          loadError={loadError}
        />
      ) : null}
    </section>
  );
}

function EntriesList({
  config,
  entries,
  loading,
  loadError,
}: {
  config: CompetitionConfig;
  entries: CompetitionPublicEntry[] | null;
  loading: boolean;
  loadError: string | null;
}) {
  const publicFields = config.fields.filter((f) => f.publiclyVisible);
  const titleFieldId =
    publicFields.find((f) => f.id !== config.fields[0]?.id && f.type === "text")?.id ??
    publicFields[1]?.id ??
    publicFields[0]?.id;
  const nameFieldId = config.fields[0]?.id;
  const descFieldId = publicFields.find((f) => f.type === "longtext")?.id;
  const flagFields = publicFields.filter((f) => f.type === "boolean");
  const count = entries?.length ?? 0;

  return (
    <div className="mt-16">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-accent inline-flex items-center gap-2">
            <Eye className="h-3.5 w-3.5" strokeWidth={1.75} />
            Who's competing
          </p>
          <h3 className="display-title mt-2 text-2xl font-light">
            {count} {count === 1 ? "entry" : "entries"} so far
          </h3>
        </div>
        <p className="text-xs text-subtle max-w-xs text-right">
          Names and entries shown publicly. Contact details stay private.
        </p>
      </div>

      {loading ? (
        <EntriesSkeleton />
      ) : loadError ? (
        <div className="surface-card p-6 text-sm text-muted">
          Couldn't load entries right now. Refresh in a moment.
        </div>
      ) : !entries || entries.length === 0 ? (
        <div className="surface-card p-8 text-center">
          <p className="font-display text-lg font-light">No entries yet</p>
          <p className="mt-1 text-sm text-muted">
            Be the first to register your {config.entryNoun}.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map((entry, i) => {
            const title = titleFieldId ? String(entry[titleFieldId] ?? "") : "";
            const name = nameFieldId ? String(entry[nameFieldId] ?? "") : "";
            const desc = descFieldId ? String(entry[descFieldId] ?? "") : "";
            return (
              <article
                key={entry.id}
                className="surface-card relative overflow-hidden p-5 transition-all hover:border-accent/40"
              >
                <div
                  aria-hidden
                  className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-accent/10 blur-2xl"
                />
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-accent-soft text-sm font-medium text-accent-ink dark:text-accent">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs uppercase tracking-[0.14em] text-subtle">
                    {config.kind ?? config.entryNoun}
                  </span>
                </div>
                <h4 className="mt-4 font-display text-xl font-light leading-snug text-ink">
                  {title || `Untitled ${config.entryNoun}`}
                </h4>
                {name ? (
                  <p className="mt-1 text-sm text-muted">
                    by <span className="text-ink/80">{name}</span>
                  </p>
                ) : null}
                {desc ? (
                  <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-3">{desc}</p>
                ) : null}
                {flagFields.length ? (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {flagFields.map((f) =>
                      entry[f.id] ? (
                        <FlagPill key={f.id} field={f} />
                      ) : null,
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FlagPill({
  field,
}: {
  field: CompetitionConfig["fields"][number];
}) {
  const lower = field.label.toLowerCase();
  const Icon = lower.includes("vegan") ? Leaf : lower.includes("nut") ? Nut : Eye;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line/70 bg-surface px-2.5 py-1 text-[11px] uppercase tracking-[0.12em] text-muted">
      <Icon className="h-3 w-3 text-accent" strokeWidth={1.75} />
      {field.label}
    </span>
  );
}

function EntriesSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="surface-card p-5 animate-pulse">
          <div className="h-9 w-9 rounded-full bg-line/70" />
          <div className="mt-4 h-5 w-3/4 rounded bg-line/70" />
          <div className="mt-2 h-4 w-1/2 rounded bg-line/60" />
          <div className="mt-4 space-y-2">
            <div className="h-3 rounded bg-line/40" />
            <div className="h-3 w-5/6 rounded bg-line/40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function FormField({
  field,
  value,
  onChange,
}: {
  field: CompetitionConfig["fields"][number];
  value: string | boolean;
  onChange: (v: string | boolean) => void;
}) {
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 rounded-2xl border border-line/60 bg-surface px-4 py-3 cursor-pointer transition-colors hover:border-line">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-line accent-accent"
        />
        <span>
          <span className="block text-sm font-medium text-ink">{field.label}</span>
          {field.hint ? (
            <span className="mt-0.5 block text-xs text-subtle">{field.hint}</span>
          ) : null}
        </span>
      </label>
    );
  }
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">{field.label}</span>
      {field.type === "longtext" ? (
        <textarea
          required={field.required}
          rows={3}
          placeholder={field.placeholder}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="form-input"
        />
      ) : (
        <input
          type="text"
          required={field.required}
          placeholder={field.placeholder}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="form-input"
        />
      )}
      {field.hint ? (
        <span className="mt-1.5 block text-xs text-subtle">{field.hint}</span>
      ) : null}
    </label>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-medium text-accent-ink dark:text-accent">
        {n}
      </span>
      <span className="text-muted">{text}</span>
    </li>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
